import { Button, EmptyState, PageContainer, PageHeader } from '@osteopro/ui';
import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useAuth } from '../../lib/auth-provider.js';
import { isPlatformStaff } from '../../lib/platform-role.js';

/**
 * Garde des écrans de plateforme. Cacher l'entrée de navigation n'est pas un droit : qui tape
 * l'adresse arrive quand même, et doit être **refusé proprement** — un titre, une phrase, un
 * retour — plutôt que de tomber sur une page vide ou une redirection silencieuse qui ferait
 * croire à une panne.
 *
 * La garde n'applique rien : le serveur refuse de toute façon (`requirePlatformRole`). Elle évite
 * seulement d'envoyer une requête dont on connaît déjà l'issue, et elle explique.
 */
export function PlatformRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [, navigate] = useLocation();

  if (isPlatformStaff(user?.platformRole)) return <>{children}</>;

  return (
    <PageContainer>
      <PageHeader title={t('platform:denied.title')} />
      <EmptyState
        icon={ShieldAlert}
        title={t('platform:denied.heading')}
        description={t('platform:denied.description')}
        data-testid="state-forbidden"
        action={
          <Button
            variant="outline"
            onClick={() => {
              navigate('/dashboard');
            }}
            data-testid="link-back-dashboard"
          >
            {t('platform:denied.back')}
          </Button>
        }
      />
    </PageContainer>
  );
}
